import { objectOrRelationshipTooltipContent } from './item';
import type { DataIndexItemKind, DataIndexUsageKey } from './types';
import type { CreatableTarget } from './use-in-actions';

/**
 * What an empty usage surface offers to make, on the item page.
 *
 * Ported from the page this replaces, one rule per surface
 * (`~/code/ui/app/components/data-index-item/{campaigns,segments,newsletters,templates,snippets,layouts}/index.gts`).
 * The rules differ, and on purpose: a surface the viewer cannot create on
 * either hides its button or shows it disabled, depending on which surface
 * it is, and Design Studio offers nothing at all.
 */

type UsageEmptyActionBase = {
  label: string;
  isDisabled: boolean;
  /** Why it is disabled, where there is something to say. */
  tooltip: string | undefined;
};

/** A button that makes the record, or one that goes to where it is made. */
export type UsageEmptyAction =
  | (UsageEmptyActionBase & { target: CreatableTarget; href?: undefined })
  | (UsageEmptyActionBase & { target: 'template' | 'snippet' | 'layout'; href: string });

export type UsageEmptyActionScope = {
  kind: DataIndexItemKind;
  /** Builder plans have no segments, automations, templates or layouts to make. */
  isBuilder: boolean;
  /** Answers the create permission for each surface that asks for one. */
  can: (target: 'segment' | 'campaign' | 'newsletter' | 'snippet' | 'layout') => boolean;
  hrefs: { snippet: string; starters: string };
};

export function usageEmptyAction(surface: DataIndexUsageKey, scope: UsageEmptyActionScope): UsageEmptyAction | null {
  const availability = (target: Parameters<typeof objectOrRelationshipTooltipContent>[1]) => {
    const { disabled, content } = objectOrRelationshipTooltipContent(scope.kind, target);
    return { isDisabled: disabled, tooltip: content };
  };

  switch (surface) {
    // Hidden without the permission, and on a Builder plan.
    case 'campaigns':
      return scope.can('campaign') && !scope.isBuilder
        ? { target: 'campaign', label: 'Create automation', ...availability('campaign') }
        : null;
    case 'segments':
      return scope.can('segment') && !scope.isBuilder
        ? { target: 'segment', label: 'Create segment', ...availability('segment') }
        : null;
    case 'layouts':
      return scope.can('layout') && !scope.isBuilder
        ? { target: 'layout', label: 'Create layout', href: scope.hrefs.starters, ...availability('layout') }
        : null;
    // Hidden on a Builder plan only: templates ask for no permission here.
    case 'templates':
      return scope.isBuilder
        ? null
        : { target: 'template', label: 'Create template', href: scope.hrefs.starters, ...availability('template') };
    // Always shown, and disabled without the permission.
    case 'newsletters': {
      const { isDisabled, tooltip } = availability('newsletter');
      return {
        target: 'newsletter',
        label: 'Create one-time send',
        isDisabled: isDisabled || !scope.can('newsletter'),
        tooltip,
      };
    }
    case 'snippets': {
      const { isDisabled, tooltip } = availability('snippet');
      return {
        target: 'snippet',
        label: 'Create snippet',
        href: scope.hrefs.snippet,
        isDisabled: isDisabled || !scope.can('snippet'),
        tooltip,
      };
    }
    case 'design_studio':
      return null;
    default: {
      const _exhaustive: never = surface;
      return _exhaustive;
    }
  }
}
